import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Box, ClickAwayListener, IconButton, InputBase, Paper, Popper, Tooltip, Typography,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import { ReactComponent as MenuCollapseIcon } from "../assets/images/menu-collapse.svg";
import { ReactComponent as MenuExpandIcon } from "../assets/images/menu-expand.svg";
import searchIcon from "../assets/images/icon-search.png";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { withAuth } from "./AuthWrapper";
import { mapAcModulesToMenuItems } from "../acMenu/mapAcModulesToMenuItems";
import { useConfigStore } from "../store/config";
import { useMenuStore } from "../store/menu";
import { buildSideMenuItems, MenuItem } from "./SideMenu/menuConfig";
import styles from "./ModuleNavLauncher.module.scss";

/** Keep L1/L2 hierarchy while filtering by keyword (always-expanded preview). */
export const filterPreviewMenu = (keywords: string, menuList: MenuItem[]): MenuItem[] => {
  const kw = keywords.toLowerCase().trim();
  return menuList
    .filter((menu) => menu.visible !== false)
    .map((menu) => {
      if (menu.children?.length) {
        const children = menu.children.filter((child) => {
          if (child.visible === false) return false;
          if (!kw) return true;
          return child.label.toLowerCase().includes(kw);
        });
        return { ...menu, children };
      }
      return menu;
    })
    .filter((menu) => {
      if (!kw) return true;
      const selfMatch = menu.label.toLowerCase().includes(kw);
      const hasMatchingChildren = Boolean(menu.children?.length);
      return selfMatch || hasMatchingChildren;
    });
};

const ModuleNavLauncher: React.FC<{ authList?: string[] }> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [keywords, setKeywords] = useState("");

  const [sideMenuOpen, sideMenuHidden, setSideMenuOpen, setSideMenuHidden] =
    useMenuStore((state) => [
      state.sideMenuOpen, state.sideMenuHidden, state.setSideMenuOpen, state.setSideMenuHidden,
    ]);
  const [language, matchedAcMenus] = useConfigStore((state) => [
    state.language,
    state.matchedAcMenus,
  ]);

  const menuItems = useMemo(
    () => [
      ...buildSideMenuItems(t, props.authList || []),
      ...mapAcModulesToMenuItems(matchedAcMenus, language),
    ],
    [t, props.authList, matchedAcMenus, language]
  );
  const filtered = useMemo(
    () => filterPreviewMenu(keywords, menuItems),
    [menuItems, keywords]
  );

  const closePreview = () => { setPreviewOpen(false); setKeywords(""); };

  const restoreSidebar = () => {
    setSideMenuHidden(false);
    setSideMenuOpen(true);
    closePreview();
  };

  const handleLauncherClick = () => {
    if (sideMenuHidden) setPreviewOpen((p) => !p);
    else if (sideMenuOpen) setSideMenuOpen(false);
    else setSideMenuHidden(true);
  };

  const handleItemClick = (item: MenuItem) => {
    if (!item.path) return;
    navigate(item.path);
    closePreview();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.code === "KeyN" && useMenuStore.getState().sideMenuHidden) {
        event.preventDefault();
        restoreSidebar();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sideMenuHidden && previewOpen) closePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideMenuHidden]);

  // ClickAwayListener cannot observe clicks inside iframes (events stay in the child document).
  // When focus moves into an iframe, the parent window blurs — close the preview then.
  useEffect(() => {
    if (!previewOpen || !sideMenuHidden) return;
    const handleWindowBlur = () => {
      const active = document.activeElement;
      if (active instanceof HTMLIFrameElement) closePreview();
    };
    window.addEventListener("blur", handleWindowBlur);
    return () => window.removeEventListener("blur", handleWindowBlur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewOpen, sideMenuHidden]);

  const label =
    sideMenuHidden && !previewOpen ? t("menu.showMenu") : t("menu.hideSideMenu");

  const renderParentIcon = (item: MenuItem) => {
    if (item.iconSrc) {
      return <img src={item.iconSrc} alt="" />;
    }
    return typeof item.icon === "string" ? <i className={item.icon} /> : item.icon;
  };

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          ref={buttonRef}
          className="nav-launcher-btn"
          data-testid="module-nav-launcher"
          aria-label={label}
          onClick={handleLauncherClick}
          sx={{ color: "var(--brand-header-text, #fff)" }}
        >
          {sideMenuHidden ? (
            <AppsIcon />
          ) : (
            <MenuCollapseIcon className="nav-collapse-icon" width={20} height={20} />
          )}
        </IconButton>
      </Tooltip>

      <Popper
        open={previewOpen && sideMenuHidden}
        anchorEl={buttonRef.current}
        placement="bottom-start"
        style={{ zIndex: 1001 }}
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
      >
        <ClickAwayListener
          onClickAway={(e) => {
            if (buttonRef.current && buttonRef.current.contains(e.target as Node)) return;
            closePreview();
          }}
        >
          <Paper
            elevation={0}
            className={styles["nav-preview-panel"]}
            data-testid="nav-preview-panel"
            onKeyDown={(e) => { if (e.key === "Escape") closePreview(); }}
          >
            <Box className={styles["nav-preview-search"]}>
              <img
                src={searchIcon}
                alt=""
                width={18}
                height={18}
                aria-hidden
                className={styles["nav-preview-search-icon"]}
              />
              <InputBase
                autoFocus
                sx={{ flex: 1, padding: "4px 0 !important" }}
                inputProps={{ "data-testid": "nav-preview-search" }}
                placeholder={t("menu.searchModule")}
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
              />
            </Box>

            <Box className={styles["nav-preview-body"]}>
              {filtered.map((item) => {
                const visibleChildren =
                  item.children?.filter((child) => child.visible !== false) ?? [];
                const isNavigable = Boolean(item.path);

                return (
                  <Box key={item.menuID} className={styles["nav-preview-group"]}>
                    <Box
                      className={styles["nav-preview-parent"]}
                      role={isNavigable ? "link" : undefined}
                      onClick={() => isNavigable && handleItemClick(item)}
                      data-navigable={isNavigable ? "true" : "false"}
                    >
                      {renderParentIcon(item)}
                      <span>{item.label}</span>
                    </Box>
                    {visibleChildren.map((child) => (
                      <Box
                        key={child.menuID}
                        role="link"
                        className={styles["nav-preview-child"]}
                        onClick={() => handleItemClick(child)}
                      >
                        {child.label}
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>

            <Box className={styles["nav-preview-footer"]}>
              <button
                type="button"
                className={styles["nav-preview-footer-action"]}
                data-testid="nav-preview-restore"
                onClick={restoreSidebar}
              >
                <MenuExpandIcon className="nav-collapse-icon" width={18} height={18} />
                <Typography component="span">{t("menu.showNavigationSidebar")}</Typography>
              </button>
              <span className={styles["nav-preview-footer-shortcut"]}>Alt/Option + N</span>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default withAuth(ModuleNavLauncher);
